﻿import React, { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import TickerPicker from "./TickerPicker.jsx";
import { getStockStats, refreshSummaries, useSummariesVersion } from "../utils/priceData.js";
import { getCompany, useCompanyInfoVersion } from "../utils/companyInfo.js";
import { loadUserData, saveUserData } from "../utils/userStore.js";

const money = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function PortfolioTracker({ user }) {
  useSummariesVersion();
  useCompanyInfoVersion();
  const [holdings, setHoldings] = useState(() => loadUserData("portfolio", user, []));
  const [ticker, setTicker] = useState("");
  const [shares, setShares] = useState("");
  const [cost, setCost] = useState("");

  // keep prices current while this page is open
  const key = holdings.map((h) => h.ticker).join(",");
  useEffect(() => {
    if (!key) return undefined;
    const list = [...new Set(key.split(","))];
    const id = setInterval(() => refreshSummaries(list), 60000);
    return () => clearInterval(id);
  }, [key]);

  const commit = (next) => {
    setHoldings(next);
    saveUserData("portfolio", user, next);
  };

  const addHolding = (e) => {
    e.preventDefault();
    const s = parseFloat(shares);
    const c = parseFloat(cost);
    if (!ticker || !(s > 0) || !(c > 0)) return;
    commit([...holdings, { id: Date.now().toString(36), ticker, shares: s, cost: c }]);
    setShares("");
    setCost("");
  };

  const rows = holdings.map((h) => {
    const company = getCompany(h.ticker);
    const cmp = num(getStockStats(h.ticker).current);
    const invested = h.shares * h.cost;
    const value = cmp === null ? null : h.shares * cmp;
    return { ...h, name: company ? company.name : h.ticker, cmp, invested, value, pnl: value === null ? null : value - invested };
  });

  const priced = rows.filter((r) => r.value !== null);
  const invested = priced.reduce((t, r) => t + r.invested, 0);
  const value = priced.reduce((t, r) => t + r.value, 0);
  const pnl = value - invested;
  const pnlPct = invested ? (pnl / invested) * 100 : 0;
  const cls = (n) => (n >= 0 ? "change-up" : "change-down");

  return (
    <main className="page-bg">
      <div className="dash-main-content screens-wrap">
        <h1 className="page-title">Portfolio Tracker</h1>
        <p className="text-muted mb-4">Record your holdings and see how your portfolio is doing.</p>

        <div className="row g-3 mb-4">
          {[
            { label: "Invested", text: money(invested) },
            { label: "Current value", text: money(value) },
            { label: "Profit / Loss", text: `${pnl >= 0 ? "+" : ""}${money(pnl)} (${pnlPct.toFixed(1)}%)`, color: cls(pnl) },
          ].map((s) => (
            <div key={s.label} className="col-12 col-md-4">
              <div className="card tool-card">
                <div className="text-muted small">{s.label}</div>
                <div className={`wl-price ${s.color || ""}`}>{s.text}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card tool-card mb-4">
          <form className="tool-form" onSubmit={addHolding}>
            <div>
              <label className="tool-label">Company</label>
              <TickerPicker value={ticker} onChange={setTicker} />
            </div>
            <div>
              <label className="tool-label">Shares</label>
              <input type="number" min="0" step="any" className="form-control" value={shares}
                onChange={(e) => setShares(e.target.value)} placeholder="0" required />
            </div>
            <div>
              <label className="tool-label">Avg. buy price ($)</label>
              <input type="number" min="0" step="0.01" className="form-control" value={cost}
                onChange={(e) => setCost(e.target.value)} placeholder="0.00" required />
            </div>
            <button type="submit" className="btn btn-purple tool-submit" disabled={!ticker}>
              <Plus size={15} /> ADD HOLDING
            </button>
          </form>
        </div>

        <div className="card wl-table-card">
          <div className="table-responsive">
            <table className="table wl-table mb-0">
              <thead>
                <tr>
                  <th className="wl-th-plain">Company</th>
                  <th className="wl-th-plain text-end">Shares</th>
                  <th className="wl-th-plain text-end">Avg. cost $</th>
                  <th className="wl-th-plain text-end">CMP $</th>
                  <th className="wl-th-plain text-end">Value $</th>
                  <th className="wl-th-plain text-end">P&amp;L $</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <a className="wl-company" href={`#/stock/${r.ticker}`}>{r.name}</a>
                      <span className="text-muted small ms-2">{r.ticker}</span>
                    </td>
                    <td className="text-end">{r.shares}</td>
                    <td className="text-end">{r.cost.toFixed(2)}</td>
                    <td className="text-end">{r.cmp === null ? "—" : r.cmp.toFixed(2)}</td>
                    <td className="text-end">{r.value === null ? "—" : r.value.toFixed(2)}</td>
                    <td className={`text-end fw-bold ${r.pnl === null ? "" : cls(r.pnl)}`}>
                      {r.pnl === null ? "—" : `${r.pnl >= 0 ? "+" : ""}${r.pnl.toFixed(2)}`}
                    </td>
                    <td className="text-end">
                      <button className="row-remove" onClick={() => commit(holdings.filter((h) => h.id !== r.id))} aria-label="Remove holding">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="wl-empty text-muted">No holdings yet. Add your first one above.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-muted small wl-note">
            Prices come from your database and are about 15 minutes delayed. They refresh every minute while this page is open.
            {priced.length < rows.length ? " Holdings without a price yet are left out of the totals." : ""}
          </p>
        </div>
      </div>
    </main>
  );
}
